/**
 * RAG Articles API Client
 * Handles all API calls related to RAG articles and chunks
 * 
 * Note: routedFetch returns parsed JSON data, not a Response object
 */

import { routedFetch } from './api-router.js';

/**
 * Get indexed articles with filters and sorting
 * @param {string} worldId - World ID
 * @param {object} options - Query options
 * @returns {Promise<{articles: Array, total: number, limit: number, offset: number}>}
 */
export async function getIndexedArticles(worldId, options = {}) {
  const {
    limit = 50,
    offset = 0,
    state = null,
    titleSearch = null,
    entityModel = null,
    startDate = null,
    endDate = null,
    sortBy = 'indexed_at',
    sortOrder = 'DESC',
  } = options;

  const params = new URLSearchParams();
  params.append('worldId', worldId);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  if (state) params.append('state', state);
  if (titleSearch) params.append('titleSearch', titleSearch);
  if (entityModel) params.append('entityModel', entityModel);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (sortBy) params.append('sortBy', sortBy);
  if (sortOrder) params.append('sortOrder', sortOrder);

  const url = `/api/rag/indexed-articles?${params.toString()}`;
  console.log('[RAG API] Fetching articles:', url);

  try {
    // routedFetch returns parsed JSON data directly, not a Response object
    const data = await routedFetch(url);

    console.log('[RAG API] Articles loaded successfully:', {
      articlesCount: data?.articles?.length,
      total: data?.total,
      success: data?.success,
    });

    if (!data || !Array.isArray(data.articles)) {
      throw new Error('Invalid response format: missing articles array');
    }

    return data;
  } catch (error) {
    console.error('[RAG API] Error in getIndexedArticles:', {
      message: error?.message,
      url,
    });
    throw error;
  }
}

/**
 * Get chunks for a specific article
 * @param {string} articleId - Article ID
 * @returns {Promise<{chunks: Array, total: number}>}
 */
export async function getArticleChunks(articleId) {
  const params = new URLSearchParams();
  params.append('articleId', articleId);

  const url = `/api/rag/indexed-article-chunks?${params.toString()}`;
  console.log('[RAG API] Fetching chunks:', url);

  try {
    // routedFetch returns parsed JSON data directly, not a Response object
    const data = await routedFetch(url);

    console.log('[RAG API] Chunks loaded successfully:', {
      chunksCount: data?.chunks?.length,
      total: data?.total,
    });

    if (!data || !Array.isArray(data.chunks)) {
      throw new Error('Invalid response format: missing chunks array');
    }

    return data;
  } catch (error) {
    console.error('[RAG API] Error in getArticleChunks:', {
      message: error?.message,
      url,
    });
    throw error;
  }
}
